/*
 * Copyright (c) 2020. American Express Company
 */

import React, { useState } from 'react';
import _ from 'lodash';
import { GlyphLinkOut, IconActivity, IconSetting } from '@americanexpress/dls-react';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import FormatService from 'src/services/format.service';
import UtilsService from 'src/services/utils.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { GraphDataPointModel } from 'src/types/graph-daata-point.model';
import { TransactionSummary } from 'src/types/transaction.model';
import { CategoryModel } from 'src/types/category.model';
import { TypographyVariants } from 'src/types/typography-variants.enum';

import Typography from 'src/components/typography/Typography';
import Drawer from 'src/components/drawer/Drawer';
import BwPieChart from 'src/bw/bw-drawer/common/BwPieChart';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import {
    ExpensesByCategoryProps,
    ExpensesViewModel,
    YourTopExpensesViewModel
} from 'src/types/view-models/expensesDrawer.view.model';

import layout from 'src/styles/layout.module.scss';
import colors from 'src/styles/colors.module.scss';
import styles from './expensesDrawer.module.scss';
import { RecurringExpenseModel } from 'src/types/recurring-expense.model';
import NavigationService from 'src/services/navigation.service';
import Selectors from 'src/utils/selectors';

const YourTopExpenses = ({ top5, hexColors, total }: YourTopExpensesViewModel): JSX.Element => (
    <div className={layout.drawerSection}>
        <Typography variant={TypographyVariants.BODY_BOLD}>
            {LiteralsService.getLiteral('ExpensesDrawer.topExpenses')}
        </Typography>
        <div className={styles.chartContainer}>
            <ul>
                {top5.map((e: GraphDataPointModel, i: number) => (
                    <li key={`${styles.chartContainer}${i}`}>
                        <Circle diameter="12px" style={{ backgroundColor: hexColors[i] }} />
                        <Typography variant={TypographyVariants.SUBTEXT}>{e.name}</Typography>
                        <Typography variant={TypographyVariants.SUBTEXT}>
                            {FormatService.formatPercent((100 * e.value) / total)}
                        </Typography>
                    </li>
                ))}
            </ul>
            <BwPieChart data={top5} width={160} hexColors={hexColors} />
        </div>
    </div>
);

const ExpensesByCategory = ({
    total,
    recurring,
    topCategories,
    subtotals,
    handleOpenTransactions,
    summaries,
    categories,
    startDate,
    endDate,
    additionalSpending,
    history
}: ExpensesByCategoryProps): JSX.Element => {
    const [expandedIndexes, setExpandedIndexes] = useState<{ [i: number]: boolean }>({});
    return (
        <div className={styles.expensesByCategory}>
            <Typography variant={TypographyVariants.HEADING_XS} className={colors.textStrong}>
                {LiteralsService.getLiteral('ExpensesDrawer.expensesByCategory')}
            </Typography>
            <ul className={styles.outer}>
                {total !== recurring &&
                    topCategories.map((category: any, i: number) => {
                        const summary: TransactionSummary = _.find(summaries, { id: category.id });
                        if (!summary) return <></>;
                        const subCategories = _.filter(categories, { parentId: category.id });
                        const openCategoryTransactions = (): any =>
                            handleOpenTransactions(category.id, startDate, endDate);
                        const total = FormatService.format(summary.total);
                        const expanded = !!expandedIndexes[i];

                        return (
                            <li key={`expensesOuterLi${i}`}>
                                <div>
                                    <Anchor
                                        onClick={openCategoryTransactions}
                                        data-cy={Selectors.PlanAhead.CATEGORY_ANCHOR}
                                        typographyProps={{
                                            variant: TypographyVariants.BODY_BOLD
                                        }}
                                    >
                                        {category.name}
                                    </Anchor>

                                    <Typography variant={TypographyVariants.HEADING_S}>{total}</Typography>
                                    <ClickableIcon icon={<IconActivity />} onClick={openCategoryTransactions} />
                                </div>
                                <Anchor
                                    onClick={() => setExpandedIndexes({ ...expandedIndexes, [i]: !expanded })}
                                    data-cy={Selectors.PlanAhead.EXPENSES_EXPAND}
                                    typographyProps={{ variant: TypographyVariants.BODY }}
                                >
                                    {expanded ? 'Hide' : 'Show'} subcategories
                                </Anchor>
                                {expanded && (
                                    <ul className={styles.inner} data-cy={Selectors.PlanAhead.CATEGORY_LIST}>
                                        {subCategories.map((e: CategoryModel, i: number) => {
                                            const openSubcategoryTransactions = (): any =>
                                                handleOpenTransactions(e.id, startDate, endDate);
                                            const subTotal = _.find(subtotals, { name: e.name });
                                            return (
                                                subTotal?.value && (
                                                    <li key={`expensesInnerLi${i}`}>
                                                        <Anchor
                                                            onClick={openSubcategoryTransactions}
                                                            data-cy={Selectors.PlanAhead.SUBCATEGORY_ANCHOR}
                                                            typographyProps={{
                                                                variant: TypographyVariants.SUBTEXT_BOLD
                                                            }}
                                                        >
                                                            {e.name}
                                                        </Anchor>

                                                        <Typography variant={TypographyVariants.SUBTEXT}>
                                                            {FormatService.format(subTotal ? subTotal.value : 0)}
                                                        </Typography>
                                                        <ClickableIcon
                                                            icon={<IconActivity />}
                                                            onClick={openSubcategoryTransactions}
                                                        />
                                                    </li>
                                                )
                                            );
                                        })}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                {additionalSpending?.monthlyAmount && additionalSpending?.monthlyAmount !== 0 ? (
                    <li>
                        <div>
                            <Anchor
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        history,
                                        `drawer/expense/${additionalSpending?.id}`,
                                        'Update Expenses'
                                    )
                                }
                                data-cy={Selectors.PlanAhead.ADDITIONAL_EXPENSES_ANCHOR}
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD
                                }}
                            >
                                {LiteralsService.getLiteral('ExpensesDrawer.additionalExpenses')}
                            </Anchor>

                            <Typography variant={TypographyVariants.HEADING_S}>
                                {additionalSpending?.monthlyAmount * 12}
                            </Typography>
                            <ClickableIcon
                                icon={<IconSetting />}
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        history,
                                        `drawer/expense/${additionalSpending?.id}`,
                                        'Update Expenses'
                                    )
                                }
                            />
                        </div>
                    </li>
                ) : null}
            </ul>
        </div>
    );
};

export default function ExpensesDrawer({
    drawerProps,
    hasFamily = false,
    handleBack,
    handleOpenTransactions
}: ExpensesViewModel): JSX.Element {
    const { summaries, recurringExpenses, startDate, categories, endDate, history } = drawerProps;
    let subtotals: GraphDataPointModel[] = summaries
        ? summaries.map((summary: any) => {
              return {
                  name: summary.name,
                  value: summary.total
              };
          })
        : [];
    subtotals = _.orderBy(subtotals, ['value'], ['desc']);
    subtotals = _.reject(subtotals, (cat: any) => cat.name === 'Overall');
    const top5: GraphDataPointModel[] = subtotals.slice(0, 5);
    const top5Name: string[] = top5.map((element: { [x: string]: any }) => {
        return element.name;
    });
    const overall = _.find(summaries, { name: 'Overall' });
    const hexColors = UtilsService.getColorListForStringList(top5Name);
    const recurring: number = _.sumBy(recurringExpenses, 'monthlyAmount') * 12;
    const topCategories: CategoryModel[] = _.filter(categories, (cat: CategoryModel) => cat.parentId == null);
    const total = overall.total || 0;
    const totalFormatted = FormatService.format(total);
    const _additionalSpending: RecurringExpenseModel = recurringExpenses.filter(
        (expense: RecurringExpenseModel) => expense.name === 'Additional'
    )[0];

    return (
        <Drawer
            title={LiteralsService.getLiteral(hasFamily ? 'ExpensesDrawer.familyTitle' : 'ExpensesDrawer.title')}
            withBackButton
            handleBack={handleBack}
            drawerProps={{ history }}
        >
            <div className={layout.drawerSection}>
                <Typography variant={TypographyVariants.HEADING_XS} className={colors.textStrong}>
                    {LiteralsService.getLiteral('ExpensesDrawer.last12')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} className={layout.block}>
                    {LiteralsService.getLiteral('ExpensesDrawer.transactionHistory')}
                </Typography>
                <Anchor
                    data-cy={Selectors.PlanAhead.LEARN_MORE}
                    typographyProps={{
                        variant: TypographyVariants.SUBTEXT,
                        className: `${layout.block} ${layout.noUnderline}`
                    }}
                >
                    {LiteralsService.getLiteral('ExpensesDrawer.learnMore')}
                    <GlyphLinkOut />
                </Anchor>
            </div>
            <div className={layout.drawerSection}>
                <Typography variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('ExpensesDrawer.totalSpending')}
                </Typography>
                <Typography className={colors.textStrong} variant={TypographyVariants.HEADING_XL}>
                    {totalFormatted}
                </Typography>
            </div>
            <YourTopExpenses top5={top5} hexColors={hexColors} total={total} />
            <ExpensesByCategory
                total={total}
                recurring={recurring}
                topCategories={topCategories}
                subtotals={subtotals}
                handleOpenTransactions={handleOpenTransactions}
                summaries={summaries}
                categories={categories}
                startDate={startDate}
                endDate={endDate}
                additionalSpending={_additionalSpending}
                history={history}
            />
        </Drawer>
    );
}
